import { bestSsnMatch, cleanLienType, formatAddress, formatDate, lienStatus, maskedSsn, titleCase } from '../BusinessDetailReport/businessReportModel'
import { moreFromLexis } from './moreFromLexis'

const list = (value) => (Array.isArray(value) ? value : value ? [value] : [])
const text = (value) => (value == null || typeof value === 'object' ? '' : String(value)).trim()
const upper = (value) => text(value).toUpperCase()
const dateValue = (date) => (date && typeof date === 'object' && date.Year ? new Date(date.Year, (date.Month || 1) - 1, date.Day || 1).getTime() : 0)

const KINDS = [
    [/FEDERAL TAX|FED TAX|IRS/, 'Federal tax lien', true],
    [/STATE TAX|ST TAX/, 'State tax lien', true],
    [/COUNTY TAX|CITY TAX|TAX WARRANT|TAX LIEN/, 'Tax lien', true],
    [/FORCIBLE|EVICT|LANDLORD TENANT/, 'Eviction', false],
    [/CHILD SUPPORT/, 'Child support lien', false],
    [/SMALL CLAIM/, 'Small claims judgment', false],
    [/CIVIL NEW FILING|CIVIL SUIT|FORECLOSURE NEW FILING/, 'Lawsuit filed', false],
    [/FORECLOSURE/, 'Foreclosure judgment', false],
    [/GARNISH/, 'Garnishment', false],
    [/MECHANIC/, "Mechanic's lien", false],
    [/HOSPITAL|MEDICAL/, 'Hospital lien', false],
    [/JUDG/, 'Judgment', false],
]
const ENDING = /RELEASE|RELEAS\b|SATISF|DISMISS|VACAT|WITHDRAW|TERMINATION|FILED IN ERROR/

const kindOf = (types) => {
    const original = types.find(t => !ENDING.test(t)) || types[0] || ''
    const match = KINDS.find(([pattern]) => pattern.test(original)) || KINDS.find(([pattern]) => pattern.test(types.join(' | ')))
    return match ? { label: match[1], tax: match[2] } : { label: cleanLienType(original) || 'Lien or judgment', tax: false }
}

const statusOf = (types, releaseDate) => {
    const all = types.join(' | ')
    if (/SATISF/.test(all) && !/RELEASE/.test(all)) return 'Satisfied'
    return lienStatus(types, releaseDate)
}
const CLOSED = ['Released', 'Satisfied', 'Dismissed', 'Vacated', 'Filed in error']

const amountOf = (value) => Number(text(value).replace(/[^0-9.]/g, '')) || 0
const money = (value) => (value > 0 ? value.toLocaleString('en-US', { style: 'currency', currency: 'USD' }).replace(/\.00$/, '') : '')
const nameCase = (value) => titleCase(value).replace(/(^|\s)([a-z])(?=\s|$)/g, (m, space, letter) => space + letter.toUpperCase())
const partyName = (p) => nameCase(p?.CompanyName || [p?.Name?.First, p?.Name?.Middle, p?.Name?.Last, p?.Name?.Suffix].filter(Boolean).join(' '))
const originName = (value) => {
    const [last, first] = text(value).split(',').map(s => s.trim())
    return nameCase(first ? `${first} ${last}` : last)
}

export const buildLien = (lien, owner = {}, now = new Date()) => {
    const filings = list(lien?.Filings?.Filing).filter(f => f && typeof f === 'object')
    const types = filings.map(f => upper(f.Type)).filter(Boolean).concat(lien?.OriginFilingType ? [upper(lien.OriginFilingType)] : [])
    const kind = kindOf(types)
    const status = statusOf(types, lien?.ReleaseDate)
    const open = !CLOSED.includes(status)
    const creditors = list(lien?.Creditors?.Creditor).map(c => titleCase(c?.Name) || list(c?.ParsedParties?.Party).map(partyName).find(Boolean)).filter(Boolean)
    const debtors = list(lien?.Debtors?.Debtor).map(d => {
        const parties = list(d?.ParsedParties?.Party)
        return { name: originName(d?.OriginName) || parties.map(partyName).find(Boolean) || '', ssn: parties.map(p => p?.SSN).find(Boolean) }
    })
    const ssnMatch = bestSsnMatch(debtors.map(d => d.ssn), owner?.SSN)
    const amount = amountOf(lien?.Amount)
    const filed = lien?.OriginFilingDate || filings.map(f => f.OriginFilingDate || f.Date).find(d => dateValue(d))
    const first = filings[0] || {}
    const county = titleCase(first.AgencyCounty)
    const where = [county && `${county} County`, titleCase(lien?.FilingJurisdictionName) || first.AgencyState || lien?.FilingJurisdiction].filter(Boolean).join(', ')
    const eviction = upper(lien?.Eviction).startsWith('Y') || kind.label === 'Eviction'
    const years = dateValue(filed) ? (now.getTime() - dateValue(filed)) / (365.25 * 86400000) : null
    const endedBy = filings.filter(f => ENDING.test(upper(f.Type))).pop()

    return {
        more: moreFromLexis(lien, 'LiensJudgments/LienJudgment[]'),
        open, status, kind: kind.label, tax: kind.tax, eviction, amount, filedAt: dateValue(filed),
        recent: years != null && years <= 2,
        title: [kind.label, creditors[0]].filter(Boolean).join(' · '),
        facts: [money(amount), filed && `Filed ${formatDate(filed)}`, where].filter(Boolean).join(' · '),
        chips: [
            open ? { label: 'Open', tone: kind.tax || (years != null && years <= 7) ? 'declined' : 'watch' }
                : { label: [status, formatDate(lien?.ReleaseDate)].filter(Boolean).join(' '), tone: 'neutral' },
            eviction && kind.label !== 'Eviction' && { label: 'Eviction', tone: 'watch' },
            ssnMatch === 'different' && { label: 'SSN differs', tone: 'watch' },
        ].filter(Boolean),
        details: [
            ['Same person?', ssnMatch === 'same' ? "A debtor's SSN matches the owner" : ssnMatch === 'prefix' ? "A debtor's SSN starts with the same 5 digits as the owner's"
                : ssnMatch === 'different' ? "No debtor's SSN matches the owner"
                : 'Lexis matched this lien by name; no SSN to compare'],
            ['Status', open ? 'Open (no release, satisfaction or dismissal on file)'
                : [status, lien?.ReleaseDate && formatDate(lien.ReleaseDate), endedBy && `(${titleCase(endedBy.Type)})`].filter(Boolean).join(' ')],
            ['Owed to', creditors.join('; ')],
            ['Amount', money(amount)],
            ['Filed', formatDate(filed)],
            ['Where', where],
            ['Eviction', eviction ? 'Yes' : ''],
            ['Debtors', debtors.map(d => [d.name, maskedSsn(d.ssn) && `SSN ${maskedSsn(d.ssn)}`].filter(Boolean).join(' · ')).filter(Boolean).join('; ')],
            ['Multiple defendants', upper(lien?.MultipleDefendant) === 'TRUE' || upper(lien?.MultipleDefendant).startsWith('Y') ? 'Yes' : ''],
            ['Filing number', text(lien?.OriginFilingNumber || first.Number)],
            ['Case number', text(lien?.CaseNumber)],
            ['Certificate number', text(lien?.CertificateNumber)],
            ['IRS serial number', text(lien?.IRSSerialNumber)],
            ['Debtor attorney', list(lien?.DebtorAttorneys?.Attorney).map(a => titleCase(a?.Name) || list(a?.ParsedParties?.Party).map(partyName).find(Boolean)).filter(Boolean).join('; ')],
            ['Creditor address', list(lien?.Creditors?.Creditor).map(c => formatAddress(c?.Address)).filter(Boolean).join('; ')],
        ].filter(([, value]) => value).map(([label, value]) => ({ label, value })),
        blocks: filings.map((f, n) => ({
            title: `${filings.length > 1 ? `Filing ${n + 1}: ` : 'Filing: '}${titleCase(f.Type) || 'Type not given'}`,
            details: [
                ['Date', formatDate(f.Date)],
                ['Number', text(f.Number)],
                ['Office', [titleCase(f.Agency), titleCase(f.AgencyCity), f.AgencyState].filter(Boolean).join(', ')],
                ['Book and page', [f.Book && `book ${text(f.Book)}`, f.Page && `page ${text(f.Page)}`].filter(Boolean).join(', ')],
            ].filter(([, value]) => value).map(([label, value]) => ({ label, value })),
        })),
    }
}

export function buildLiensJudgments(liens, owner = {}, now = new Date()) {
    const rows = list(liens).filter(l => l && typeof l === 'object').map(l => buildLien(l, owner, now))
    const byDate = (a, b) => b.filedAt - a.filedAt
    const open = rows.filter(r => r.open).sort(byDate)
    const closed = rows.filter(r => !r.open).sort(byDate)
    return {
        rows, open, closed,
        summary: {
            open: open.length,
            openAmount: open.reduce((sum, r) => sum + r.amount, 0),
            recent: open.filter(r => r.recent).length,
            closed: closed.length,
            evictions: rows.filter(r => r.eviction).length,
            warn: open.some(r => r.tax || r.chips[0].tone === 'declined'),
        },
    }
}

export const formatTotal = money
